class MixMatchStorage {
  constructor(config) {
    this.config = config;

    this._products = []; // [{ handle, quantity }]
    this._variantDays = 30;
    this._purchaseType = 'subscription'; // 'subscription' | 'onetimer'

    // Derived — always recalculated, never set directly
    this.giftProducts = [];
    this.discountTier = 0;
    this.discountPercentage = 0;
    this.totalPrice = 0;
    this.totalCompareAtPrice = 0;
    this.totalDiscount = 0;
    this.averageBasePricePerKg = 0;
  }

  // ---- Getters ----

  get products() { return this._products; }
  get variantDays() { return this._variantDays; }
  get purchaseType() { return this._purchaseType; }

  get totalQuantity() {
    return this._products.reduce((sum, p) => sum + p.quantity, 0);
  }

  get canCheckout() {
    return this.totalQuantity >= this.config.min_products;
  }

  // ---- Mutations ----

  setProductQuantity(handle, quantity) {
    const qty = parseInt(quantity);
    const idx = this._products.findIndex(p => p.handle === handle);
    const prevTier = this.discountTier;

    if (qty <= 0) {
      if (idx !== -1) this._products.splice(idx, 1);
    } else if (idx !== -1) {
      this._products[idx].quantity = qty;
    } else {
      this._products.push({ handle, quantity: qty });
    }

    this._recalc();

    if (this.discountTier !== prevTier) {
      publish(PUB_SUB_EVENTS.reachedToTier, { tier: this.discountTier, ...this.snapshot() });
    }

    publish(PUB_SUB_EVENTS.quantityUpdate, this.snapshot());
  }

  setVariantDays(days) {
    this._variantDays = parseInt(days);
    this._recalc();
    publish(PUB_SUB_EVENTS.variantChange, this.snapshot());
  }

  setPurchaseType(type) {
    this._purchaseType = type;
    // onetimer is only available on the 90-day variant
    if (type === 'onetimer') this._variantDays = 90;
    this._recalc();
    publish(PUB_SUB_EVENTS.variantChange, this.snapshot());
  }

  // Sets both days and type atomically — use this from the variant picker to avoid two events.
  setVariant(days, type) {
    if (type === 'onetimer') {
      this._purchaseType = 'onetimer';
      this._variantDays = 90;
    } else {
      this._purchaseType = 'subscription';
      this._variantDays = parseInt(days);
    }
    this._recalc();
    publish(PUB_SUB_EVENTS.variantChange, this.snapshot());
  }

  // ---- Queries ----

  // Returns the variant config object for a handle given current variantDays.
  // Falls back to '30' if the selected days aren't configured for that product.
  getVariantData(handle) {
    const productConfig = this.config.product[handle];
    if (!productConfig) return null;
    return productConfig[this._variantDays] ?? productConfig['30'] ?? null;
  }

  // Returns product display info (title + image) — stored alongside variant data in config.product.
  getProductInfo(handle) {
    return this.config.product[handle] ?? null;
  }

  // Weighted average €/kg across selected units (same formula as product-variant-base-price.liquid).
  getAverageBasePricePerKg() {
    let sum = 0;
    let totalQty = 0;

    for (const p of this._products) {
      const variant = this.getVariantData(p.handle);
      if (!variant?.weight) continue;

      let unitPrice = variant.price;
      if (this._purchaseType === 'subscription' && variant.selling_plan?.length) {
        unitPrice = variant.selling_plan[0].per_delivery_price;
      }

      const perKg = _calcBasePricePerKg(unitPrice, variant.weight);
      if (perKg <= 0) continue;

      sum += perKg * p.quantity;
      totalQty += p.quantity;
    }

    if (totalQty === 0) return 0;
    return Math.round((sum / totalQty) * 100) / 100;
  }

  // Per-card unit pricing (same unit basis as _recalcPrices) for the current variant + bundle discount tier.
  getProductCardPricing(handle) {
    const variant = this.getVariantData(handle);
    if (!variant) return null;

    let unitPrice = variant.price;
    if (this._purchaseType === 'subscription' && variant.selling_plan?.length) {
      unitPrice = variant.selling_plan[0].per_delivery_price;
    }

    const unitDiscounted = Math.round(unitPrice * (1 - this.discountPercentage / 100));

    return {
      unitPriceCents: unitPrice,
      unitDiscountedCents: unitDiscounted,
      discountPercentage: this.discountPercentage,
    };
  }

  // Builds the line items array ready for the cart API.
  getCartItems() {
    const items = [];

    for (const p of this._products) {
      const variant = this.getVariantData(p.handle);
      if (!variant) continue;

      const item = { id: variant.id, quantity: p.quantity };
      if (this._purchaseType === 'subscription' && variant.selling_plan?.length) {
        item.selling_plan = variant.selling_plan[0].id;
      }
      items.push(item);
    }

    for (const gift of this.giftProducts) {
      items.push({ id: gift.id, quantity: 1 });
    }

    return items;
  }

  // Two-batch checkout: (1) paid lines with _gifts / _parent_uid / _purchase_type per unit,
  // (2) free gift lines with _bought_with / _uid matching the parent uid (same pattern as buy-buttons + product-gifts-inputs).
  // Quantities are expanded to quantity:1 per bundle unit so each pair gets a unique _parent_uid / _uid.
  buildMixMatchCheckoutPlan() {
    const units = [];
    for (const p of this._products) {
      const variant = this.getVariantData(p.handle);
      if (!variant) continue;
      for (let q = 0; q < p.quantity; q++) {
        units.push({ handle: p.handle, variant });
      }
    }

    const base = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    const paidItems = [];
    const giftItems = [];

    for (let i = 0; i < units.length; i++) {
      const u = units[i];
      const uid = `${u.handle}-${base}-${i}`;

      const paid = {
        id: u.variant.id,
        quantity: 1,
        properties: {
          _gifts: 'true',
          _parent_uid: uid,
          _purchase_type: this._purchaseType,
        },
      };
      if (this._purchaseType === 'subscription' && u.variant.selling_plan?.length) {
        paid.selling_plan = u.variant.selling_plan[0].id;
      }
      paidItems.push(paid);

      const gift = this.giftProducts[i];
      if (gift) {
        giftItems.push({
          id: gift.id,
          quantity: 1,
          properties: {
            _bought_with: u.handle,
            _uid: uid,
          },
        });
      }
    }

    return { paidItems, giftItems };
  }

  // Returns a plain-object snapshot of the current state.
  // All pub/sub payloads use this shape.
  snapshot() {
    return {
      products: this._products.map(p => ({ ...p })),
      giftProducts: [...this.giftProducts],
      variantDays: this._variantDays,
      purchaseType: this._purchaseType,
      discountTier: this.discountTier,
      discountPercentage: this.discountPercentage,
      totalPrice: this.totalPrice,
      totalCompareAtPrice: this.totalCompareAtPrice,
      totalDiscount: this.totalDiscount,
      totalQuantity: this.totalQuantity,
      canCheckout: this.canCheckout,
      averageBasePricePerKg: this.averageBasePricePerKg,
    };
  }

  // ---- Private ----

  _recalc() {
    this._recalcTier();
    this._recalcGifts();
    this._recalcPrices();
  }

  _recalcTier() {
    const qty = this.totalQuantity;
    const {
      tier_1_discount_products_qty, tier_1_discount_value,
      tier_2_discount_products_qty, tier_2_discount_value,
    } = this.config;

    if (qty >= tier_2_discount_products_qty) {
      this.discountTier = 2;
      this.discountPercentage = tier_2_discount_value;
    } else if (qty >= tier_1_discount_products_qty) {
      this.discountTier = 1;
      this.discountPercentage = tier_1_discount_value;
    } else {
      this.discountTier = 0;
      this.discountPercentage = 0;
    }
  }

  _recalcGifts() {
    const skus = this.config.gift_products_order.split(',').map(s => s.trim());
    const count = Math.min(this.totalQuantity, skus.length);

    this.giftProducts = [];
    for (let i = 0; i < count; i++) {
      const giftData = this.config.gift_products[skus[i]];
      if (giftData) {
        this.giftProducts.push({ sku: skus[i], id: giftData.id, image: giftData.image });
      }
    }
  }

  // Prices are in cents (Shopify standard). Discount applied after purchase type resolution.
  _recalcPrices() {
    let price = 0;
    let compareAt = 0;

    for (const p of this._products) {
      const variant = this.getVariantData(p.handle);
      if (!variant) continue;

      let unitPrice = variant.price;
      if (this._purchaseType === 'subscription' && variant.selling_plan?.length) {
        unitPrice = variant.selling_plan[0].per_delivery_price;
      }

      price += Math.round(unitPrice * (1 - this.discountPercentage / 100)) * p.quantity;
      compareAt += unitPrice * p.quantity;
    }

    this.totalPrice = price;
    this.totalCompareAtPrice = compareAt;
    this.totalDiscount = compareAt - price;
    this.averageBasePricePerKg = this.getAverageBasePricePerKg();
  }
}
